import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import RoundedRectangleButton from 'src/components/RoundedRectangleButton';
import LoadingDots from 'src/assets/loading_dots.svg';

import {
	ORG_SETUP_SYNCING,
	ORG_SETUP_SYNC_ERROR,
	ORG_SETUP_COMPLETE,
} from 'src/utils/authOrg.constants';
import { anyWaiting, anyRunning, anyErrors, anyComplete } from 'src/utils/importJobs';

import { fetchImportJobs } from 'src/redux/importJobs/importJob.action';
import {
	getInitialImportJobs,
	getPendingImportJobs,
} from 'src/redux/importJobs/importJob.selector';
import { getPendingAuthOrganizations } from 'src/redux/authOrganizations/authOrganization.selector';
import { requestUpdateAuthOrganization } from 'src/redux/authOrganizations/authOrganization.action';
import styles from '../index.css';
import { Heading, Paragraph } from 'src/lib/StyledComponents';
import theme from 'src/lib/theme';

const getProgressiveTimeoutTime = index => {
	// this will hopefully make debugging less awful for us.
	// the wait times will back off over the first few waits
	const waitTimes = [
		1000, // 1 second
		1000 * 5, // 5 seconds
		1000 * 10, // 10 seconds
		1000 * 60, // 1 minute
		1000 * 60 * 10, // 10 minutes
	];
	return waitTimes[Math.min(index, waitTimes.length)];
};

class Syncing extends Component {
	updateAuthOrgState = newSetupState => {
		this.setState({ waitingForTransition: true });
		this.props.requestUpdateAuthOrganization(this.props.authOrganizations[0]._id, {
			setup_state: newSetupState,
		});
	};

	completeOrgSetup = () => {
		this.updateAuthOrgState(ORG_SETUP_COMPLETE);
	};

	constructor(props) {
		super(props);
		this.state = { waits: 0 };
	}

	componentDidMount() {
		this.props.fetchImportJobs({ initialImport: true });
	}

	componentWillUnmount(props) {
		if (this.state.fetchTimeoutId) {
			clearTimeout(this.state.fetchTimeoutId);
		}
	}

	componentDidUpdate() {
		const jobs = this.props.initialImportJobs;

		// skip this if our props are missing, or we're mid-request,
		// or everything's done, or we're waiting intentionally
		if (
			!this.props.authOrganizations ||
			!this.props.pendingImportJobs ||
			!jobs ||
			this.props.pendingImportJobs.fetch ||
			this.props.pendingAuthOrganizations.update ||
			this.state.allComplete ||
			this.state.waitingToCheck ||
			this.state.waitingForTransition
		) {
			return;
		}

		if (jobs.length > 1 || anyErrors(jobs)) {
			// if we have error jobs or more than one job, that's bad
			this.updateAuthOrgState(ORG_SETUP_SYNC_ERROR);
		} else if (anyComplete(jobs)) {
			// if we have a completed job, that's good -- gives the user a button to move on
			this.setState({ allComplete: true });
		} else if (anyWaiting(jobs) || anyRunning(jobs)) {
			// if we have a job that's waiting or running, we will check again in a moment
			const fetchTimeoutId = setTimeout(() => {
				this.props.fetchImportJobs({ initialImport: true });
				this.setState({ waitingToCheck: false });
			}, getProgressiveTimeoutTime(this.state.waits));

			// mark down that we're waiting so we don't queue additional timeouts
			this.setState({
				waitingToCheck: true,
				waits: Math.min(this.state.waits + 1, 3),
				fetchTimeoutId,
			});
		}
	}

	render() {
		// return to landing page if auth org in wrong state
		if (this.props.authOrganizations[0].setup_state !== ORG_SETUP_SYNCING) {
			return <Redirect to={{ pathname: '/' }} />;
		}

		let component = (
			<React.Fragment>
				<Heading {...theme.heading.setUpTitle}>Syncing...</Heading>
				<Paragraph>This may take up to a day.</Paragraph>
				<Paragraph>
					We'll send an email to <strong>{this.props.currentAuthUser.email}</strong> when
					the next step is ready.
				</Paragraph>
				<Paragraph>
					Feel free to leave this window open and wait, but you don't need to! Your sync
					is safe.
				</Paragraph>
				<LoadingDots
					style={{
						width: '40px',
						margin: '0 auto',
						display: 'block',
						marginBottom: '25px',
					}}
				/>
			</React.Fragment>
		);

		if (this.state.allComplete) {
			component = (
				<React.Fragment>
					<Heading {...theme.heading.setUpTitle}>Sync complete!</Heading>
					<Paragraph>Your data is imported.</Paragraph>
					<RoundedRectangleButton
						className={styles.DefaultButton}
						fontSize="16px"
						onClick={this.completeOrgSetup}
					>
						Setup my user
					</RoundedRectangleButton>
				</React.Fragment>
			);
		}

		return <div className={styles.SetupCardLayout}>{component}</div>;
	}
}

const mapStateToProps = state => ({
	initialImportJobs: getInitialImportJobs(state),
	pendingImportJobs: getPendingImportJobs(state),
	pendingAuthOrganizations: getPendingAuthOrganizations(state),
});

const actions = {
	fetchImportJobs,
	requestUpdateAuthOrganization,
};

Syncing = connect(mapStateToProps, actions)(Syncing);

export default Syncing;



// WEBPACK FOOTER //
// ./src/ConnectedComponents/Setup/Syncing/index.js